import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Upload,
  message,
} from 'antd';
import { PlusOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { api, http } from '../../api/client';
import { adminAlertStyle } from '../../styles/adminPageShell';

interface Anchor {
  range: string;
  level: string;
  desc: string;
}
interface CompetencyItem {
  id: number;
  name: string;
  definition: string;
  levels: Anchor[];
  category: string | null;
  enabled: boolean;
  sortOrder: number;
  source: string | null;
}
interface ImportResult {
  total: number;
  created: number;
  updated: number;
  errors: Array<{ row: number; message: string }>;
}

const ANCHORS = [
  { range: '0-1', level: '待发展' },
  { range: '2-3', level: '胜任' },
  { range: '4-5', level: '优秀' },
  { range: '6-7', level: '卓越' },
];

export function CompetencyItemsSection() {
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<CompetencyItem | null>(null);
  const [form] = Form.useForm();
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<UploadFile | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [searchQ, setSearchQ] = useState('');

  const { data: items, isLoading } = useQuery({
    queryKey: ['competency-items'],
    queryFn: () => api.get<unknown, CompetencyItem[]>('/admin/competency-items'),
  });

  const saveMutation = useMutation({
    mutationFn: (vals: Record<string, string>) => {
      const levels = ANCHORS.map((a) => ({
        range: a.range,
        level: a.level,
        desc: vals[a.level] || '',
      }));
      const payload = { name: vals.name, definition: vals.definition || '', levels };
      return editing
        ? api.patch(`/admin/competency-items/${editing.id}`, payload)
        : api.post('/admin/competency-items', payload);
    },
    onSuccess: () => {
      message.success(editing ? '已更新' : '已新增');
      setEditOpen(false);
      setEditing(null);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['competency-items'] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (v: { id: number; enabled: boolean }) =>
      api.patch(`/admin/competency-items/${v.id}`, { enabled: v.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['competency-items'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/competency-items/${id}`),
    onSuccess: () => {
      message.success('已删除');
      qc.invalidateQueries({ queryKey: ['competency-items'] });
    },
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setEditOpen(true);
  };
  const openEdit = (r: CompetencyItem) => {
    setEditing(r);
    const vals: Record<string, string> = { name: r.name, definition: r.definition };
    ANCHORS.forEach((a) => {
      vals[a.level] = r.levels?.find((l) => l.range === a.range)?.desc ?? '';
    });
    form.setFieldsValue(vals);
    setEditOpen(true);
  };

  const downloadTemplate = async () => {
    try {
      const r = await http.get('/admin/competency-items/import-template.xlsx', {
        responseType: 'blob',
      });
      const blob = new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '素质项库导入模版.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error(`模版下载失败：${(e as Error).message}`);
    }
  };

  const doImport = async () => {
    if (!importFile?.originFileObj) {
      message.warning('请先选择 .xls 或 .xlsx 文件');
      return;
    }
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile.originFileObj as Blob);
      const result = (await api.post<unknown, ImportResult>(
        '/admin/competency-items/import-excel',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )) as unknown as ImportResult;
      setImportResult(result);
      message.success(
        `导入完成：新增 ${result.created} / 更新 ${result.updated}${
          result.errors.length ? ` / 错误 ${result.errors.length}` : ''
        }`,
      );
      qc.invalidateQueries({ queryKey: ['competency-items'] });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(`导入失败：${err?.response?.data?.message ?? err?.message ?? '未知错误'}`);
    } finally {
      setImportBusy(false);
    }
  };

  const filteredItems = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return items ?? [];
    return (items ?? []).filter(
      (it) =>
        it.name.toLowerCase().includes(q) ||
        (it.definition || '').toLowerCase().includes(q),
    );
  }, [items, searchQ]);

  const columns = [
    { title: '素质项', dataIndex: 'name', width: 140 },
    { title: '定义', dataIndex: 'definition', ellipsis: true },
    {
      title: '分档',
      dataIndex: 'levels',
      width: 80,
      render: (lv: Anchor[]) => `${(lv || []).filter((l) => l.desc).length}/4`,
    },
    {
      title: '启用',
      dataIndex: 'enabled',
      width: 80,
      render: (en: boolean, r: CompetencyItem) => (
        <Switch
          checked={en}
          loading={toggleMutation.isPending}
          onChange={(v) => toggleMutation.mutate({ id: r.id, enabled: v })}
        />
      ),
    },
    {
      title: '操作',
      width: 130,
      render: (_: unknown, r: CompetencyItem) => (
        <Space>
          <a onClick={() => openEdit(r)}>编辑</a>
          <Popconfirm title="确认删除该素质项？" onConfirm={() => deleteMutation.mutate(r.id)}>
            <a style={{ color: '#ff4d4f' }}>删除</a>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={adminAlertStyle}
        message="素质项"
        description={
          <ul style={{ margin: '4px 0 0 18px', padding: 0, fontSize: 12 }}>
            <li>维护维度库引用的素质项名称、定义及 4 档行为锚点（待发展 / 胜任 / 优秀 / 卓越）。</li>
            <li>支持 Excel 批量导入；停用后不会出现在维度库「从素质项库添加」列表中。</li>
          </ul>
        }
      />
      <Card
        style={{ marginTop: 16 }}
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
              下载模版
            </Button>
            <Button
              icon={<UploadOutlined />}
              onClick={() => {
                setImportFile(null);
                setImportResult(null);
                setImportOpen(true);
              }}
            >
              导入 Excel
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              新增素质项
            </Button>
          </Space>
        }
      >
        <Input.Search
          placeholder="搜索素质项名称或定义"
          allowClear
          style={{ marginBottom: 12, maxWidth: 360 }}
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
        />
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={filteredItems}
          columns={columns}
          pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 项` }}
        />
      </Card>

      <Modal
        title={editing ? '编辑素质项' : '新增素质项'}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saveMutation.isPending}
        destroyOnClose
        width={680}
      >
        <Form form={form} layout="vertical" onFinish={(v) => saveMutation.mutate(v)}>
          <Form.Item name="name" label="素质项名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如：先公后私" />
          </Form.Item>
          <Form.Item name="definition" label="素质项定义">
            <Input.TextArea rows={2} placeholder="该素质项的定义" />
          </Form.Item>
          {ANCHORS.map((a) => (
            <Form.Item key={a.range} name={a.level} label={`${a.level}（${a.range} 分）`}>
              <Input.TextArea rows={2} placeholder={`${a.level}档的行为描述`} />
            </Form.Item>
          ))}
        </Form>
      </Modal>

      <Modal
        title="导入素质项库（Excel）"
        open={importOpen}
        onCancel={() => setImportOpen(false)}
        onOk={doImport}
        confirmLoading={importBusy}
        okText="开始导入"
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="按模版列填写：序号 / 素质项 / 定义 / 0-1分(待发展) / 2-3分(胜任) / 4-5分(优秀) / 6-7分(卓越)。同名素质项会被覆盖更新。支持 .xls 与 .xlsx。"
          />
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
            下载导入模版
          </Button>
          <Upload.Dragger
            maxCount={1}
            beforeUpload={() => false}
            fileList={importFile ? [importFile] : []}
            onChange={(info) => setImportFile(info.fileList[info.fileList.length - 1] ?? null)}
            accept=".xls,.xlsx"
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p>点击或拖拽 .xls / .xlsx 文件到此处</p>
          </Upload.Dragger>
          {importResult && (
            <Alert
              type={importResult.errors.length ? 'warning' : 'success'}
              showIcon
              message={`新增 ${importResult.created} / 更新 ${importResult.updated} / 共 ${importResult.total} 行`}
              description={
                importResult.errors.length ? (
                  <div style={{ maxHeight: 160, overflow: 'auto' }}>
                    {importResult.errors.map((er, i) => (
                      <div key={i}>
                        第 {er.row} 行：{er.message}
                      </div>
                    ))}
                  </div>
                ) : undefined
              }
            />
          )}
        </Space>
      </Modal>
    </>
  );
}
